// frontend/src/pages/Services/index.jsx
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Table, Input, Select, Space, Tag, Typography,
  Row, Col, Button, Tabs, Progress, Card, Statistic,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, AppstoreOutlined,
  UserOutlined, WarningOutlined, CheckCircleOutlined, PlusOutlined,
  KeyOutlined, UserAddOutlined, StopOutlined,
} from '@ant-design/icons';
import { servicesAPI } from '../../api/client';
import ServiceDrawer from './ServiceDrawer';
import AccessAssignmentModal from './AccessAssignmentModal';
import LicensePurchaseModal from './LicensePurchaseModal';
import ServiceFormModal from './ServiceFormModal';
import CategoryManagerModal from './CategoryManagerModal';
import BulkRevokeModal from './BulkRevokeModal';
import ExpiringTab from './ExpiringTab';
import AnalyticsTab from './AnalyticsTab';
import { ALLOCATION_COLORS, ALLOCATION_LABELS, formatDate, daysUntil } from './constants';
import TableToolbar from '../../components/TableToolbar';
import ExportButton from '../../components/ExportButton';
import useTableSort from '../../hooks/useTableSort';
import { makeTablePagination, normalizeTablePagination } from '../../utils/tablePagination';
import { usePermission } from '../../hooks/usePermission';

const { Title, Text } = Typography;

function LicenseBar({ used, total }) {
  if (!total)
    return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
  const pct    = Math.min(Math.round((used / total) * 100), 100);
  const status = pct >= 100 ? 'exception' : pct >= 80 ? 'normal' : 'success';
  return (
    <Space direction="vertical" size={2} style={{ minWidth: 170 }}>
      <Progress percent={pct} size="small" status={status} showInfo={false} />
      <Space size={14}>
        <Text style={{ fontSize: 12 }}>Используется: <Text strong>{used}</Text></Text>
        <Text style={{ fontSize: 12 }}>Куплено: <Text strong>{total}</Text></Text>
      </Space>
    </Space>
  );
}

function PurchasedLicenseUsage({ row }) {
  const purchasedGroups = Number(row.purchased_groups || 0);
  if (!purchasedGroups) {
    return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
  }

  const mode = row.allocation_mode || 'named_user';
  const total = Number(row.purchased_licenses || 0);
  const used = Number(row.purchased_used_licenses || 0);

  if (mode === 'shared_account') {
    return (
      <Space direction="vertical" size={0}>
        <Text strong>{`Аккаунтов: ${total}`}</Text>
        <Text>{`Пользователей: ${used}`}</Text>
      </Space>
    );
  }

  if (mode === 'concurrent') {
    return (
      <Space direction="vertical" size={0}>
        <Text strong>{`Куплено: ${total}`}</Text>
        <Text>{`Пользователей: ${used}`}</Text>
      </Space>
    );
  }

  return <LicenseBar used={used} total={total} />;
}

function LicensingType({ row }) {
  if (!Number(row.purchased_groups || 0)) {
    return <Text type="secondary">—</Text>;
  }
  const mode = row.allocation_mode || 'named_user';
  return (
    <Tag color={ALLOCATION_COLORS[mode]}>
      {ALLOCATION_LABELS[mode] || mode}
    </Tag>
  );
}

function LicensingLegend() {
  return (
    <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
      <Space wrap size={[8, 6]}>
        <Text strong>Как читать лицензии:</Text>
        <Tag color={ALLOCATION_COLORS.named_user}>{ALLOCATION_LABELS.named_user}</Tag>
        <Text type="secondary">1 пользователь занимает 1 купленную лицензию</Text>
        <Tag color={ALLOCATION_COLORS.concurrent}>{ALLOCATION_LABELS.concurrent}</Tag>
        <Text type="secondary">пользователей может быть больше числа купленных лицензий</Text>
        <Tag color={ALLOCATION_COLORS.shared_account}>{ALLOCATION_LABELS.shared_account}</Tag>
        <Text type="secondary">общий аккаунт для нескольких пользователей</Text>
        <Tag color="orange">Грязный домен</Tag>
        <Text type="secondary">активные доступы отдельно от закупок и продлений</Text>
      </Space>
    </Card>
  );
}

function ServicesTable() {
  const [search, setSearch]                 = useState('');
  const [categoryId, setCategoryId]         = useState(null);
  const [licenseType, setLicenseType]       = useState(null);
  const [allocationMode, setAllocationMode] = useState(null);
  const [selected, setSelected]             = useState(null);
  const [showAssign, setShowAssign]         = useState(false);
  const [showRevoke, setShowRevoke]         = useState(false);
  const [showAdd, setShowAdd]               = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const { canEdit } = usePermission('services');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'name',
    initialSortDir: 'asc',
    onSort: () => setPagination(p => ({ ...p, current: 1 })),
  });

  const { data: categories } = useQuery({
    queryKey: ['service-categories'],
    queryFn:  () => servicesAPI.getCategories().then(r => r.data),
  });

  const queryParams = useMemo(() => ({
    search:          search          || undefined,
    category_id:     categoryId      || undefined,
    license_type:    licenseType     || undefined,
    allocation_mode: allocationMode  || undefined,
    sort_by:         sortBy,
    sort_dir:        sortDir,
  }), [search, categoryId, licenseType, allocationMode, sortBy, sortDir]);

  const { data: services, isLoading, refetch } = useQuery({
    queryKey: ['services', queryParams],
    queryFn:  () => servicesAPI.getAll(queryParams).then(r => r.data),
  });

  const hasFilters = Boolean(search || categoryId || licenseType || allocationMode);
  const resetFilters = () => {
    setSearch('');
    setCategoryId(null);
    setLicenseType(null);
    setAllocationMode(null);
    setPagination(p => ({ ...p, current: 1 }));
  };

  const columns = [
    {
      title: `Сервис${sortIcon('name')}`, dataIndex: 'name', key: 'name', width: 200,
      onHeaderCell: () => getSortableHeaderProps('name'),
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          {r.vendor && <Text type="secondary" style={{ fontSize: 11 }}>{r.vendor}</Text>}
        </Space>
      ),
    },
    {
      title: `Категория${sortIcon('category_name')}`, dataIndex: 'category_name', key: 'category', width: 140,
      onHeaderCell: () => getSortableHeaderProps('category_name'),
      render: (v, r) => v
        ? <Tag color={r.category_color}>{v}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Тип лицензирования', key: 'allocation_mode', width: 175,
      render: (_, r) => <LicensingType row={r} />,
    },
    {
      title: `Купленные лицензии${sortIcon('purchased')}`, key: 'purchased', width: 200,
      onHeaderCell: () => getSortableHeaderProps('purchased'),
      render: (_, r) => <PurchasedLicenseUsage row={r} />,
    },
    {
      title: `Грязный домен${sortIcon('grey')}`, dataIndex: 'grey_users', key: 'grey', width: 115, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('grey'),
      render: (v) => Number(v || 0) > 0
        ? <Tag color="orange">{v}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: `Пользователей всего${sortIcon('used')}`, dataIndex: 'used_licenses', key: 'used', width: 140, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('used'),
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: `Истекает${sortIcon('expiry')}`, dataIndex: 'nearest_expiry', key: 'expiry', width: 120,
      onHeaderCell: () => getSortableHeaderProps('expiry'),
      render: (v) => {
        if (!v) return '—';
        const days = daysUntil(v);
        return <Tag color={days <= 30 ? 'red' : days <= 90 ? 'orange' : 'green'}>{formatDate(v)}</Tag>;
      },
    },
  ];

  return (
    <>
      <TableToolbar>
        <Space wrap size={8}>
          <Input placeholder="Поиск по сервису" prefix={<SearchOutlined />}
            style={{ width: 230 }} value={search} allowClear
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }} />
          <Select placeholder="Категория" style={{ width: 170 }} value={categoryId} allowClear
            onChange={v => { setCategoryId(v); setPagination(p => ({ ...p, current: 1 })); }}
            options={(categories || []).map(c => ({ label: c.name, value: c.id }))} />
          <Select placeholder="Тип лицензирования" style={{ width: 190 }} value={allocationMode} allowClear
            onChange={v => { setAllocationMode(v); setPagination(p => ({ ...p, current: 1 })); }}
            options={Object.entries(ALLOCATION_LABELS).map(([value, label]) => ({ label, value }))} />
          <Select placeholder="Доступы" style={{ width: 210 }} value={licenseType} allowClear
            onChange={v => { setLicenseType(v); setPagination(p => ({ ...p, current: 1 })); }}
            options={[
              { label: 'Есть купленные лицензии', value: 'purchased' },
              { label: 'Есть грязный домен', value: 'grey' },
              { label: 'Купленные + грязный домен', value: 'mixed' },
            ]} />
          {hasFilters && <Button onClick={resetFilters}>Сбросить</Button>}
          <ExportButton
            onExport={(fileFormat) => servicesAPI.exportList(queryParams, fileFormat)}
            filenameBase="services"
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Обновить</Button>
          {canEdit && (
            <Button icon={<AppstoreOutlined />} onClick={() => setShowServiceForm(true)}>Новый сервис</Button>
          )}
          {canEdit && (
            <Button onClick={() => setShowCategories(true)}>Категории</Button>
          )}
          <Button icon={<PlusOutlined />} onClick={() => setShowAdd(true)}>Добавить лицензии</Button>
          <Button type="primary" icon={<UserAddOutlined />} onClick={() => setShowAssign(true)}>Выдать</Button>
          <Button danger icon={<StopOutlined />} onClick={() => setShowRevoke(true)}>Отозвать</Button>
        </Space>
      </TableToolbar>

      <LicensingLegend />

      <Card size="small">
        <Table
          dataSource={services || []} columns={columns}
          rowKey="id" loading={isLoading} size="small" scroll={{ x: 1150 }}
          pagination={makeTablePagination({
            ...pagination,
            total: (services || []).length,
            showTotal: t => `Всего ${t} сервисов`,
          })}
          onChange={(pag) => setPagination(normalizeTablePagination(pag))}
          onRow={record => ({ onClick: () => setSelected(record), style: { cursor: 'pointer' } })}
        />
      </Card>

      <ServiceDrawer service={selected} onClose={() => setSelected(null)} />

      <ServiceFormModal
        open={showServiceForm}
        onClose={() => setShowServiceForm(false)}
      />
      <CategoryManagerModal
        open={showCategories}
        onClose={() => setShowCategories(false)}
      />
      <LicensePurchaseModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        selectService
        onSuccess={() => refetch()}
      />
      <AccessAssignmentModal
        open={showAssign}
        onClose={() => setShowAssign(false)}
        onSuccess={() => refetch()}
      />
      <BulkRevokeModal
        open={showRevoke}
        onClose={() => setShowRevoke(false)}
        onSuccess={() => refetch()}
      />
    </>
  );
}

export default function ServicesPage() {
  const { data: stats } = useQuery({
    queryKey: ['services-stats'],
    queryFn:  () => servicesAPI.getStats().then(r => r.data),
  });

  return (
    <div style={{ padding: '0 0 24px' }}>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>Сервисы и лицензии</Title>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card size="small"><Statistic title="Сервисов" value={stats?.services_total || 0} prefix={<AppstoreOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card size="small"><Statistic title="Активных назначений" value={stats?.active_assignments || 0}
            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card size="small"><Statistic title="Пользователей с доступом" value={stats?.employees_with_access || 0}
            prefix={<UserOutlined style={{ color: '#1677ff' }} />} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card size="small"><Statistic title="Доступов через грязный домен" value={stats?.grey_licenses || 0}
            prefix={<KeyOutlined style={{ color: stats?.grey_licenses > 0 ? '#fa8c16' : '#8c8c8c' }} />} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card size="small"><Statistic title="Истекают в 30 дней" value={stats?.expiring_soon || 0}
            prefix={<WarningOutlined style={{ color: stats?.expiring_soon > 0 ? '#ff4d4f' : '#8c8c8c' }} />} /></Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="services"
        items={[
          { key: 'services',   label: 'Сервисы',    children: <ServicesTable /> },
          { key: 'expiring',   label: 'Истекают',   children: <ExpiringTab /> },
          { key: 'analytics',  label: 'Аналитика',  children: <AnalyticsTab /> },
        ]}
      />
    </div>
  );
}
